import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BrandingEditingProvider, EditablePhoto, useFieldEditing } from '@kevlarcon/pages';
import { authApi, TenantBranding } from '../api/auth';
import { applyBrandingSave, SiteBranding, TitleTextTransform } from '../api/site';
import { useSetBrandPreview } from '../contexts/BrandPreviewContext';

interface Props {
  open: boolean;
  onClose: () => void;
}

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '4px', marginTop: '18px',
};

const sizeRowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: '10px' };

const numberInputStyle: React.CSSProperties = {
  width: '64px', padding: '6px 8px', fontSize: '13px', border: '1px solid #ccc', borderRadius: '4px',
};

const selectStyle: React.CSSProperties = {
  width: '100%', padding: '6px 10px', fontSize: '13px', border: '1px solid #ccc', borderRadius: '4px',
};

const TRANSFORM_OPTIONS: { value: TitleTextTransform; label: string }[] = [
  { value: '', label: 'Normal' },
  { value: 'uppercase', label: 'All caps' },
  { value: 'small-caps', label: 'Small caps' },
];

/** A slider paired with a number input for a pixel size that can also be
 * blanked back to "use the default" — `null` while the slider has no opinion
 * yet vs. a driven value once the admin touches either control. */
const PixelSizeField: React.FC<{
  testId: string;
  value: number | null;
  defaultValue: number;
  min: number;
  max: number;
  onChange: (next: number | null) => void;
}> = ({ testId, value, defaultValue, min, max, onChange }) => {
  const effective = value ?? defaultValue;
  return (
    <div style={sizeRowStyle}>
      <input
        type="range"
        data-testid={`${testId}-slider`}
        min={min}
        max={max}
        value={effective}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ flex: 1 }}
      />
      <input
        type="number"
        data-testid={`${testId}-input`}
        min={min}
        max={max}
        value={effective}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        style={numberInputStyle}
      />
      {value !== null && (
        <button
          type="button"
          data-testid={`${testId}-reset-button`}
          onClick={() => onChange(null)}
          title="Use the default size"
          style={{ fontSize: '11px', padding: '5px 8px', border: '1px solid #ccc', borderRadius: '4px', background: 'white', cursor: 'pointer' }}
        >
          Reset
        </button>
      )}
    </div>
  );
};

const BrandStyleForm: React.FC<{ branding: TenantBranding }> = ({ branding }) => {
  const { patchFields, status } = useFieldEditing();
  const { setPreview, clearPreview } = useSetBrandPreview();

  const [headerLogoHeight, setHeaderLogoHeight] = useState(branding.header_logo_height);
  const [footerLogoHeight, setFooterLogoHeight] = useState(branding.footer_logo_height);
  const [titleFontSize, setTitleFontSize] = useState(branding.title_font_size);
  const [titleTransform, setTitleTransform] = useState(branding.title_text_transform);
  const [titleBold, setTitleBold] = useState(branding.title_bold);
  const [titleItalic, setTitleItalic] = useState(branding.title_italic);

  useEffect(() => {
    setHeaderLogoHeight(branding.header_logo_height);
    setFooterLogoHeight(branding.footer_logo_height);
    setTitleFontSize(branding.title_font_size);
    setTitleTransform(branding.title_text_transform);
    setTitleBold(branding.title_bold);
    setTitleItalic(branding.title_italic);
  }, [branding]);

  // Mirrors every drag/keystroke into SiteHeader/SiteFooter immediately, via
  // BrandPreviewContext, so the page updates live while this panel is open —
  // Save is the only thing that actually commits it. Clearing on unmount
  // (panel closed, saved or not) hands rendering back to the real branding
  // value; a save has already landed that same value there by then (see
  // onSaved below), so there's nothing to revert visually in that case.
  useEffect(() => {
    setPreview({
      header_logo_height: headerLogoHeight,
      footer_logo_height: footerLogoHeight,
      title_font_size: titleFontSize,
      title_text_transform: titleTransform,
      title_bold: titleBold,
      title_italic: titleItalic,
    });
  }, [headerLogoHeight, footerLogoHeight, titleFontSize, titleTransform, titleBold, titleItalic, setPreview]);

  useEffect(() => () => clearPreview(), [clearPreview]);

  const isDirty = (
    headerLogoHeight !== branding.header_logo_height
    || footerLogoHeight !== branding.footer_logo_height
    || titleFontSize !== branding.title_font_size
    || titleTransform !== branding.title_text_transform
    || titleBold !== branding.title_bold
    || titleItalic !== branding.title_italic
  );

  const save = () => {
    patchFields([
      [['header_logo_height'], headerLogoHeight],
      [['footer_logo_height'], footerLogoHeight],
      [['title_font_size'], titleFontSize],
      [['title_text_transform'], titleTransform],
      [['title_bold'], titleBold],
      [['title_italic'], titleItalic],
    ]);
  };

  return (
    <>
      <label style={labelStyle}>Header logo height (px)</label>
      <PixelSizeField
        testId="brand-style-header-logo-height"
        value={headerLogoHeight}
        defaultValue={32}
        min={16}
        max={120}
        onChange={setHeaderLogoHeight}
      />

      <label style={labelStyle}>Footer logo height (px)</label>
      <PixelSizeField
        testId="brand-style-footer-logo-height"
        value={footerLogoHeight}
        defaultValue={32}
        min={16}
        max={160}
        onChange={setFooterLogoHeight}
      />

      <label style={labelStyle}>Footer logo (optional override)</label>
      <p style={{ fontSize: '12px', color: '#666', margin: '0 0 8px' }}>
        Mirrors the header logo automatically. Set an image here only to show a
        different one in the footer — e.g. a light variant for the dark background.
      </p>
      <EditablePhoto
        src={branding.footer_logo ?? ''}
        alt={branding.footer_logo_alt}
        srcPath={['footer_logo']}
        altPath={['footer_logo_alt']}
        maxDimensions={{ width: 800, height: 800 }}
        imgProps={{
          style: {
            height: '32px', width: 'auto', display: 'block',
            border: '1px dashed #ccc', borderRadius: '4px', padding: '4px',
            background: '#2b2320',
          },
        }}
      />

      <label style={labelStyle}>Site name font size (px)</label>
      <PixelSizeField
        testId="brand-style-title-font-size"
        value={titleFontSize}
        defaultValue={22}
        min={12}
        max={48}
        onChange={setTitleFontSize}
      />

      <label style={labelStyle}>Site name capitalization</label>
      <select
        data-testid="brand-style-title-transform-select"
        value={titleTransform}
        onChange={(e) => setTitleTransform(e.target.value as TitleTextTransform)}
        style={selectStyle}
      >
        {TRANSFORM_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>

      <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '8px' }}>
        <input
          type="checkbox"
          data-testid="brand-style-title-bold-checkbox"
          checked={titleBold}
          onChange={(e) => setTitleBold(e.target.checked)}
        />
        Bold
      </label>
      <label style={{ ...labelStyle, marginTop: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <input
          type="checkbox"
          data-testid="brand-style-title-italic-checkbox"
          checked={titleItalic}
          onChange={(e) => setTitleItalic(e.target.checked)}
        />
        Italic
      </label>

      <p style={{ fontSize: '12px', color: '#666', marginTop: '18px' }}>
        Drag the handle at the right edge of the site name in the header to
        control where it wraps to a new line.
      </p>

      <div style={{ padding: '16px 0 4px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
        {status === 'saved' && <span style={{ fontSize: '12px', color: '#5A7847', marginRight: 'auto' }}>Saved</span>}
        <button
          type="button"
          data-testid="brand-style-save-button"
          onClick={save}
          disabled={!isDirty || status === 'saving'}
          style={{
            padding: '6px 16px', borderRadius: '4px', border: 'none', cursor: isDirty ? 'pointer' : 'default',
            backgroundColor: '#839b3b', color: 'white', opacity: isDirty ? 1 : 0.5,
          }}
        >
          {status === 'saving' ? 'Saving...' : 'Save'}
        </button>
      </div>
    </>
  );
};

/**
 * The admin control for logo sizing and site-name typography, the counterpart
 * to `ThemeSettingsDrawer` for colors/fonts. Opened from `EditModeToolbar`'s
 * "Brand" button. Owns its own `BrandingEditingProvider` instance (independent
 * of `SiteHeader`/`SiteFooter`'s) so it works whether or not those are
 * currently mounted in edit mode; `onSaved` replaces the whole cached
 * `tenant_branding` object with the server's response (every branding field,
 * not just the ones this modal edits) so header and footer never end up
 * reading a stale value for a field saved through the other's provider.
 */
const BrandStyleModal: React.FC<Props> = ({ open, onClose }) => {
  const queryClient = useQueryClient();
  const { data: branding } = useQuery<TenantBranding>({
    queryKey: ['tenant_branding'],
    queryFn: authApi.getBranding,
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  if (!open || !branding) return null;

  const brandingValues: SiteBranding = {
    title: branding.name,
    logo: branding.logo ?? '',
    logo_alt: branding.logo_alt,
    header_logo_height: branding.header_logo_height,
    footer_logo: branding.footer_logo ?? '',
    footer_logo_alt: branding.footer_logo_alt,
    footer_logo_height: branding.footer_logo_height,
    title_font_size: branding.title_font_size,
    title_text_transform: branding.title_text_transform,
    title_bold: branding.title_bold,
    title_italic: branding.title_italic,
    title_max_width: branding.title_max_width,
    footer_heading: branding.footer_heading,
    footer_tagline: branding.footer_tagline,
    footer_doxology: branding.footer_doxology,
  };

  return (
    <div
      style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: '380px', maxWidth: '90vw',
        backgroundColor: 'white', boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
        zIndex: 1200, display: 'flex', flexDirection: 'column',
      }}
      data-testid="brand-style-modal"
    >
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0 }}>Brand style</h3>
        <button
          data-testid="brand-style-close-button"
          onClick={onClose}
          style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }}
        >
          ×
        </button>
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
        <BrandingEditingProvider
          endpoint="/site/branding/"
          values={brandingValues}
          active
          onSaved={(saved) => queryClient.setQueryData<TenantBranding>(
            ['tenant_branding'],
            (old) => (old ? applyBrandingSave(old, saved) : old),
          )}
        >
          <BrandStyleForm branding={branding} />
        </BrandingEditingProvider>
      </div>
    </div>
  );
};

export default BrandStyleModal;
